import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Plus, X, Eye, EyeOff } from "lucide-react";
import { deleteData, getData, patchData, postData, putData } from "../api/ApiServices";
import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import CustomAlert from "../view/common/alert/CustomAlert";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { UserData, User, Role, ApiResponse, UserFormData } from '../models/userRole/types'
import { fetchDropDownData } from "../utils/fetchDropDownData";

const customStyles = `
  .form-group {
    position: relative;
    margin-bottom: 20px;
    width: 100%;
  }
  
  .form-group input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .form-group input.input-error {
    border-color: #ff6b6b;
  }
  
  .form-error-message {
    color: #ff6b6b;
    font-size: 12px;
    margin-top: 4px;
    position: absolute;
    bottom: -18px;
    left: 0;
  }
  
  .select-container {
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
  }
  
  .select-error .react-select__control {
    border-color: #ff6b6b;
  }
  
  .item-models-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .item-models-modal {
    background-color: #fff;
    border-radius: 8px;
    width: 80%;
    max-width: 400px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  .item-models-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 10px;
    border-bottom: 1px solid #eee;
  }
  
  .item-models-modal-header h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  .item-models-modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding:3px;
  }
  
  .modal-form-container {
    padding: 15px;
    overflow-y: auto;
    max-height: calc(80vh - 50px);
  }
  
  .react-select__menu {
    z-index: 10;
  }
  
  .submit-button {
    width: 100%;
    padding: 8px;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    margin-top: 10px;
  }
  
  .submit-button:hover {
    background-color: #45a049;
  }
  
  .password-toggle-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #666;
  }
  
  .form-group.password-field {
    position: relative;
  }
  
  .form-group.password-field input {
    padding-right: 40px;
  }
`;

const UserManagement: React.FC = () => {
  const [userData, setUserData] = useState<User[]>([]);
  const [roleOptions, setRoleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [newUser, setNewUser] = useState<UserFormData>({
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    password: "",
    roleId: "",
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const excludeFieldsForRacks = ["createdBy","Role Id"];
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    contactNo?: string;
    password?: string;
    roleId?: string;
  }>({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("firstName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => { }
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => { }
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions);

  const [initializeUserManagement, setUserManagementInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    setUserManagementInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeUserManagement) {
      fetchUsers();
    }
  }, [
    initializeUserManagement,
    searchTerm,
    currentPage,
    itemsPerPage,
    filters,
    sortBy,
    sortOrder,
  ]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await getData(
        "/users",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: {
            ...filters,
            status: "ACTIVE"
          },
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      ) as ApiResponse;
      if (response?.data?.data) {
        const formattedData = response.data.data.map((user: any) => ({
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          contactNo: user.contactNo,
          roleId: user.roleId,
        }));

        setUserData(formattedData);

        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalItems(response.data.pagination.total || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      configureAlert(
        "Failed to load users data.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async (inputValue: string) => {
    const response: any = await fetchDropDownData({
      endpoint: "/roles",
      searchKey: "userType",
      searchTerm: inputValue,
      labelKey: "userType",
      valueKey: "_id",
    });

    setRoleOptions(response);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => { },
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const hasUserChanged = (): boolean => {
    if (!editingUser || !originalUser) return true;
  
    const getRoleId = (role: any) => (typeof role === 'object' && role !== null ? role._id : role);
  
    return (
      editingUser.firstName !== originalUser.firstName ||
      editingUser.lastName !== originalUser.lastName ||
      editingUser.email !== originalUser.email ||
      editingUser.contactNo !== originalUser.contactNo ||
      getRoleId(editingUser.roleId) !== getRoleId(originalUser.roleId) ||
      passwordChanged
    );
  };
  

  const validateForm = (): boolean => {
    const errors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      contactNo?: string;
      password?: string;
      roleId?: string;
    } = {};

    if (!editingUser) {
      if (!newUser.firstName.trim()) {
        errors.firstName = "First Name is required";
      }

      if (!newUser.lastName.trim()) {
        errors.lastName = "Last Name is required";
      }

      if (!newUser.email.trim()) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
        errors.email = "Invalid email format";
      }

      if (!newUser.contactNo.trim()) {
        errors.contactNo = "Contact Number is required";
      } else if (!/^\d{10,15}$/.test(newUser.contactNo)) {
        errors.contactNo = "Invalid contact number";
      }

      if (!newUser.password?.trim()) {
        errors.password = "Password is required";
      } else if (newUser.password && newUser.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }

      if (!newUser.roleId) {
        errors.roleId = "Role is required";
      }
    } else {
      if (!editingUser.firstName.trim()) {
        errors.firstName = "First Name is required";
      }

      if (!editingUser.lastName.trim()) {
        errors.lastName = "Last Name is required";
      }

      if (!editingUser.email.trim()) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editingUser.email)) {
        errors.email = "Invalid email format";
      }

      if (!editingUser.contactNo.trim()) {
        errors.contactNo = "Contact Number is required";
      } else if (!/^\d{10,15}$/.test(editingUser.contactNo)) {
        errors.contactNo = "Invalid contact number";
      }

      if (passwordChanged && (!editingUser.password || editingUser.password.length < 6)) {
        errors.password = "Password must be at least 6 characters";
      }

      if (!editingUser.roleId) {
        errors.roleId = "Role is required";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkForDuplicates = (): boolean => {
    const currentEmail = editingUser ? editingUser.email : newUser.email;
    const duplicateEmail = userData.find(
      (user) => user.email.toLowerCase() === currentEmail.toLowerCase() &&
        user._id !== (editingUser ? editingUser._id : null)
    );

    if (duplicateEmail) {
      setValidationErrors((prev) => ({
        ...prev,
        email: "Email already exists",
      }));

      configureAlert(
        "Email already exists!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return true;
    }

    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (checkForDuplicates()) {
      return;
    }

    if (editingUser && !hasUserChanged()) {
      setIsModalOpen(false);
      configureAlert(
        "No changes were made.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingUser) {
        const updatedUser: any = {
          firstName: editingUser.firstName,
          lastName: editingUser.lastName,
          email: editingUser.email,
          contactNo: editingUser.contactNo,
          roleId: editingUser.roleId
        };

        if (passwordChanged && editingUser.password) {
          updatedUser.password = editingUser.password;
        }

        const response: any = await patchData("/users", editingUser._id, updatedUser);
        if (response.data) {
          configureAlert(
            `User "${editingUser.firstName} ${editingUser.lastName}" has been updated successfully.`,
            () => {
              fetchUsers();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
      } else {
        const userData = {
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          email: newUser.email,
          contactNo: newUser.contactNo,
          password: newUser.password,
          roleId: newUser.roleId
        };

        const response: any = await postData("/users", userData);

        if (response.statusCode === 201) {
          configureAlert(
            `User "${newUser.firstName} ${newUser.lastName}" has been added successfully.`,
            () => {
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
      }

      fetchUsers();
      setIsModalOpen(false);
      setEditingUser(null);
      setOriginalUser(null);
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        contactNo: "",
        password: "",
        roleId: "",
      });
      setPasswordChanged(false);
      setShowPassword(false);
    } catch (error) {
      console.error("Error saving user:", error);
      configureAlert(
        `Failed to ${editingUser ? "update" : "add"} user.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (editingUser) {
      if (name === 'password' && value !== '') {
        setPasswordChanged(true);
      }

      setEditingUser((prev) => ({
        ...prev!,
        [name]: value,
      }));
    } else {
      setNewUser((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selected: any, { name }: any) => {
    if (editingUser) {
      setEditingUser((prev) => ({
        ...prev!,
        [name]: selected?.value || "",
      }));
    } else {
      setNewUser((prev) => ({
        ...prev,
        [name]: selected?.value || "",
      }));
    }

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleModalClose = () => {
    if (isModalOpen) {
      const hasChanges = editingUser
        ? hasUserChanged()
        : (newUser.firstName.trim() !== "" ||
          newUser.lastName.trim() !== "" ||
          newUser.email.trim() !== "" ||
          newUser.contactNo.trim() !== "" ||
          newUser.password?.trim() !== "" ||
          newUser.roleId !== "");

      if (hasChanges) {
        configureAlert(
          "Are you sure you want to close without saving?",
          () => {
            setIsModalOpen(false);
            setEditingUser(null);
            setOriginalUser(null);
            setNewUser({
              firstName: "",
              lastName: "",
              email: "",
              contactNo: "",
              password: "",
              roleId: "",
            });
            setValidationErrors({});
            setPasswordChanged(false);
            setShowPassword(false);
            closeAlert();
          },
          closeAlert,
          "Yes",
          "No",
          true,
          true,
          null
        );
      } else {
        setIsModalOpen(false);
        setEditingUser(null);
        setOriginalUser(null);
        setNewUser({
          firstName: "",
          lastName: "",
          email: "",
          contactNo: "",
          password: "",
          roleId: "",
        });
        setValidationErrors({});
        setPasswordChanged(false);
        setShowPassword(false);
      }
    }
  };

  const handleEdit = async (user: User) => {
    console.log("Editing user:", user);
    await fetchRoles("");
    const roleId = typeof user.roleId === "object" && user.roleId !== null 
      ? user.roleId._id 
      : user.roleId;
    if (roleId && !roleOptions.some(option => option.value === roleId)) {
      let roleLabel = "";
      if (typeof user.roleId === "object" && user.roleId !== null && user.roleId.userType) {
        roleLabel = user.roleId.userType;
      } else {
        roleLabel = `Role ID: ${roleId}`;
      }
      setRoleOptions(prev => [...prev, { value: roleId, label: roleLabel }]);
    }
    
    setEditingUser({
      ...user,
      password: "",
      roleId: roleId,
    });
    setOriginalUser({ ...user });
    setValidationErrors({});
    setPasswordChanged(false);
    setShowPassword(false);
    setIsModalOpen(true);
  };
  
  const handleDelete = async (id: string) => {
    const deleteUser: any = userData.find(user => user._id === id);

    configureAlert(
      `Are you sure you want to delete ${deleteUser.firstName} permanently?`,
      async () => {
        closeAlert();

        setIsLoading(true);
        try {
          await deleteData("/users", id);

          configureAlert(
            `User has been deleted successfully.`,
            () => {
              fetchUsers();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } catch (error) {
          console.error("Error deleting user:", error);
          configureAlert(
            "Failed to delete user.",
            closeAlert,
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
        setIsLoading(false);
      },
      closeAlert,
      "Yes",
      "No",
      true,
      true,
      null
    );
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleAddButtonClick = () => {
    fetchRoles("");    
    setEditingUser(null);
    setOriginalUser(null);
    setNewUser({
      firstName: "",
      lastName: "",
      email: "",
      contactNo: "",
      password: "",
      roleId: "",
    });
    setValidationErrors({});
    setPasswordChanged(false);
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const columns = [
    { key: "firstName", label: "First Name", sortable: true },
    { key: "lastName", label: "Last Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "contactNo", label: "Contact Number", sortable: true },
  ];

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>User Role</span>
            </div>
            {permission.menus?.users?.create ? (
              <div
                className="items-button-container tooltip-wrapper"
                onClick={handleAddButtonClick}
              >
                <button className="item-models-add-btn">
                  <Plus size={16} />
                </button>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={userData}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            onEdit={handleEdit}
            onDelete={handleDelete}
             excludeFields={excludeFieldsForRacks}
             context="userrole"
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editingUser ? "Edit User" : "Add New User"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-form-container">
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={editingUser ? editingUser.firstName : newUser.firstName}
                    onChange={handleInputChange}
                    className={validationErrors.firstName ? "input-error" : ""}
                    required
                  />
                  {validationErrors.firstName && (
                    <div className="form-error-message">
                      {validationErrors.firstName}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={editingUser ? editingUser.lastName : newUser.lastName}
                    onChange={handleInputChange}
                    className={validationErrors.lastName ? "input-error" : ""}
                    required
                  />
                  {validationErrors.lastName && (
                    <div className="form-error-message">
                      {validationErrors.lastName}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={editingUser ? editingUser.email : newUser.email}
                    onChange={handleInputChange}
                    className={validationErrors.email ? "input-error" : ""}
                    required
                  />
                  {validationErrors.email && (
                    <div className="form-error-message">
                      {validationErrors.email}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    name="contactNo"
                    placeholder="Contact Number"
                    value={editingUser ? editingUser.contactNo : newUser.contactNo}
                    onChange={handleInputChange}
                    className={validationErrors.contactNo ? "input-error" : ""}
                    required
                    maxLength={10}
                  />
                  {validationErrors.contactNo && (
                    <div className="form-error-message">
                      {validationErrors.contactNo}
                    </div>
                  )}
                </div>

                <div className="form-group password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={editingUser ? "New Password (optional)" : "Password"}
                    value={editingUser ? editingUser.password || "" : newUser.password || ""}
                    onChange={handleInputChange}
                    className={validationErrors.password ? "input-error" : ""}
                    required={!editingUser}
                  />

                  <div
                    className="password-toggle-icon"
                    onClick={handleTogglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                  {validationErrors.password && (
                    <div className="form-error-message">
                      {validationErrors.password}
                    </div>
                  )}
                </div>

                <div className="select-container">
                  <Select
                    name="roleId"
                    options={roleOptions}
                    value={roleOptions.find(
                      (option) => option.value === (editingUser ? editingUser.roleId : newUser.roleId)
                    )}
                    onChange={(selected, action) =>
                      handleSelectChange(selected, action)
                    }
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        fetchRoles(inputValue);
                      }
                    }}
                    placeholder="Select Role"
                    isSearchable
                    className={validationErrors.roleId ? "select-error" : ""}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {validationErrors.roleId && (
                    <div className="form-error-message">
                      {validationErrors.roleId}
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : editingUser ? "Update" : "Add"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default UserManagement;